import { ContentHeader } from "../../components";
import { Form, Button, Input, Row, Col, Table, Space, InputNumber, Select, Tag } from 'antd';
import { DeleteOutlined, SaveOutlined, EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useEffect, useState } from "react";
import Column from 'antd/es/table/Column';
import { layDsBoPhanKiemKe, luuDMBopPhanKiemKe, xoaDMBopPhanKiemKe } from "@app/services/taisan";
import ModalTextForm from "@app/components/modal/ModalTextForm";
import { toast } from "react-toastify";
import { validateMessages } from '../../helpers/common';
import { hideLoadingScreen, showLoadingScreen } from '../../store/reducers/loading';
import { useDispatch } from 'react-redux';
import { DMBopPhanKiemKe } from "../../models/taisan/TaiSan";
import { searchLength } from "../../constants";

const LOAI_DOI_TUONG_OPTIONS = [
    { label: "Phòng ban / Đơn vị", value: "PHONGBAN" },
    { label: "Lãnh đạo", value: "LANHDAO" },
    { label: "Vị trí / Khu vực phòng", value: "VITRI" },
    { label: "Cá nhân khác", value: "CANHAN" },
];

const QuanLyDMBoPhanKiemKe = () => {
    const [data, setData] = useState<Array<DMBopPhanKiemKe>>([]);
    const [page, setPage] = useState(1);
    const [tuKhoa, setTuKhoa] = useState<string>("");
    const [locNhomCap1, setLocNhomCap1] = useState<string | undefined>(undefined);
    const [reloadPage, setReloadPage] = useState<boolean>(true);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const [isLoadingBtn, setIsLoadingBtn] = useState<boolean>(false);
    const [showModalChiTiet, setShowModalChiTiet] = useState<boolean>(false);
    const [idXoa, setIdXoa] = useState<number>(0);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const setFormDetailModal = (value: DMBopPhanKiemKe | null) => {
        form.setFieldsValue({
            "id": value?.id || 0,
            "ma": value?.ma || "",
            "ten": value?.ten || "",
            "nhomCap1": value?.nhomCap1 || "",
            "nhomCap2": value?.nhomCap2 || "",
            "loaiDoiTuong": value?.loaiDoiTuong || "PHONGBAN",
            "ghiChu": value?.ghiChu || "",
            "thuTuHienThi": value?.thuTuHienThi ?? 1
        });
        setShowModalChiTiet(true);
    };

    const layDuLieu = async () => {
        try {
            setIsLoadingTable(true);
            let rs = await layDsBoPhanKiemKe();
            setData(rs || []);
            setIsLoadingTable(false);
        } catch (error: any) {
            setIsLoadingTable(false);
            toast.error(error.message || "Lỗi tải danh sách bộ phận kiểm kê");
        }
    };

    const onDelete = async () => {
        try {
            setIsLoadingBtn(true);
            const res = await xoaDMBopPhanKiemKe(idXoa);
            if (res.trangThai === 1 || res.duLieu) {
                toast.success('Xoá bộ phận kiểm kê thành công!');
                const totalRecords = data.length - 1;
                let newPage = page;
                if (newPage > Math.ceil(totalRecords / searchLength)) {
                    newPage = Math.max(1, Math.ceil(totalRecords / searchLength));
                }
                setPage(newPage);
            } else {
                toast.error(res.thongBao || 'Xoá bộ phận không thành công!');
            }
        } catch (err: any) {
            toast.error('Đã xảy ra lỗi, xoá không thành công!');
        } finally {
            setIdXoa(0);
            setReloadPage(true);
            setIsLoadingBtn(false);
        }
    };

    const onFinish = async (values: any) => {
        try {
            dispatch(showLoadingScreen());
            setIsLoadingBtn(true);
            const res = await luuDMBopPhanKiemKe(values);
            if (res.trangThai === 1 || res.duLieu > 0) {
                setShowModalChiTiet(false);
                toast.success('Lưu thông tin bộ phận kiểm kê thành công!');
            } else {
                toast.error(res.thongBao || 'Lưu không thành công!');
            }
        } catch (err: any) {
            setShowModalChiTiet(false);
            toast.error('Đã xảy ra lỗi, lưu không thành công!');
        } finally {
            dispatch(hideLoadingScreen());
            setIsLoadingBtn(false);
            setReloadPage(true);
        }
    };

    useEffect(() => {
        if (reloadPage) {
            layDuLieu();
            setReloadPage(false);
        }
    }, [reloadPage]);

    // Lấy danh sách nhóm cấp 1 duy nhất để làm filter
    const dsNhomCap1 = Array.from(new Set(data.map(x => x.nhomCap1).filter(Boolean)));

    return (
        <section>
            <ContentHeader title="Quản lý danh mục Bộ phận kiểm kê tài sản (Cột biểu mẫu 1)" />
            {idXoa > 0 && (
                <ModalTextForm
                    show={idXoa > 0}
                    title="Xoá bộ phận kiểm kê"
                    handleClose={() => setIdXoa(0)}
                    handleSave={onDelete}
                >
                    Xác nhận xóa bộ phận kiểm kê đang chọn?
                </ModalTextForm>
            )}
            {showModalChiTiet && (
                <ModalTextForm
                    title="Thông tin bộ phận kiểm kê"
                    hideFooter
                    show={showModalChiTiet}
                    handleClose={() => setShowModalChiTiet(false)}
                >
                    <Form
                        layout="vertical"
                        validateMessages={validateMessages}
                        onFinish={onFinish}
                        form={form}
                    >
                        <Form.Item hidden name="id"><Input /></Form.Item>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Mã định danh"
                                    name="ma"
                                    rules={[{ required: true, message: 'Vui lòng nhập mã định danh' }]}
                                >
                                    <Input placeholder="Ví dụ: BP_GD, BP_VP, BP_PGD_THACH" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Thứ tự hiển thị cột"
                                    name="thuTuHienThi"
                                >
                                    <InputNumber style={{ width: "100%" }} min={0} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Tên hiển thị trên cột"
                                    name="ten"
                                    rules={[{ required: true, message: 'Vui lòng nhập tên hiển thị' }]}
                                >
                                    <Input placeholder="Ví dụ: Giám đốc, VP quản lý, P. TCCB-LĐ" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Loại đối tượng"
                                    name="loaiDoiTuong"
                                    rules={[{ required: true, message: 'Vui lòng chọn loại đối tượng' }]}
                                >
                                    <Select options={LOAI_DOI_TUONG_OPTIONS} placeholder="Chọn loại đối tượng" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Nhóm cấp 1 (Header lớn)"
                                    name="nhomCap1"
                                    rules={[{ required: true, message: 'Vui lòng nhập nhóm cấp 1' }]}
                                >
                                    <Input placeholder="Ví dụ: Ban Giám đốc, Văn phòng, Phòng Tín dụng" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="Nhóm cấp 2 (Header phụ)"
                                    name="nhomCap2"
                                >
                                    <Input placeholder="Ví dụ: VP quản lý, VP sử dụng (hoặc để trống)" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Form.Item
                                    label="Ghi chú"
                                    name="ghiChu"
                                >
                                    <Input.TextArea rows={2} placeholder="Ghi chú thêm" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item>
                            <Button type="primary" loading={isLoadingBtn} className="float-right" htmlType="submit" icon={<SaveOutlined rev="" />}>
                                Lưu
                            </Button>
                        </Form.Item>
                    </Form>
                </ModalTextForm>
            )}
            <Row className="form-group" gutter={8}>
                <Col span={6}>
                    <Input
                        prefix={<SearchOutlined rev="" />}
                        placeholder="Tìm kiếm theo mã, tên..."
                        onChange={(e) => { setPage(1); setTuKhoa(e.target.value); }}
                        allowClear
                    />
                </Col>
                <Col span={6}>
                    <Select
                        placeholder="Lọc theo Nhóm cấp 1"
                        style={{ width: "100%" }}
                        allowClear
                        value={locNhomCap1}
                        onChange={(val) => { setPage(1); setLocNhomCap1(val); }}
                    >
                        {dsNhomCap1.map(n => (
                            <Select.Option key={n} value={n}>{n}</Select.Option>
                        ))}
                    </Select>
                </Col>
                <Col span={12}>
                    <Button
                        className="hrm-btn hrm-btn-warning float-right"
                        onClick={() => {
                            setFormDetailModal(null);
                        }}
                        icon={<PlusOutlined rev="" />}
                    >
                        Thêm mới
                    </Button>
                </Col>
            </Row>
            <Row gutter={8}>
                <Col span={24}>
                    <div className="card card-primary">
                        <Row>
                            <Col span={24}>
                                <Table
                                    loading={isLoadingTable}
                                    locale={{ emptyText: "Không có dữ liệu" }}
                                    dataSource={data
                                        .map((item: any, index) => { item.key = item.id || index; return item; })
                                        .filter((record: DMBopPhanKiemKe) => {
                                            const matchKw = (record.ten?.toLowerCase().includes(tuKhoa.toLowerCase()) ||
                                                             record.ma?.toLowerCase().includes(tuKhoa.toLowerCase()) ||
                                                             record.nhomCap1?.toLowerCase().includes(tuKhoa.toLowerCase()));
                                            const matchGroup = !locNhomCap1 || record.nhomCap1 === locNhomCap1;
                                            return matchKw && matchGroup;
                                        })}
                                    rowClassName={(record) => {
                                        return (record?.id === idXoa) ? "selected-row row-key-" : "";
                                    }}
                                    pagination={{
                                        current: page,
                                        pageSize: searchLength,
                                        onChange: (p) => {
                                            setPage(p);
                                        },
                                    }}
                                >
                                    <Column
                                        width="60px"
                                        title="STT"
                                        key="STT"
                                        align="center"
                                        render={(value, record, index) => (
                                            <span key={"stt" + index}>
                                                {(page - 1) * searchLength + index + 1}
                                            </span>
                                        )}
                                    />
                                    <Column width="140px" title="Mã bộ phận" dataIndex="ma" key="ma" />
                                    <Column title="Tên cột hiển thị" dataIndex="ten" key="ten" />
                                    <Column
                                        title="Nhóm cấp 1"
                                        dataIndex="nhomCap1"
                                        key="nhomCap1"
                                        render={(val) => <Tag color="blue">{val}</Tag>}
                                    />
                                    <Column
                                        title="Nhóm cấp 2"
                                        dataIndex="nhomCap2"
                                        key="nhomCap2"
                                        render={(val) => val ? <Tag color="cyan">{val}</Tag> : <span className="text-muted">-</span>}
                                    />
                                    <Column
                                        width="130px"
                                        title="Loại đối tượng"
                                        dataIndex="loaiDoiTuong"
                                        key="loaiDoiTuong"
                                        render={(val) => {
                                            const opt = LOAI_DOI_TUONG_OPTIONS.find(x => x.value === val);
                                            return <Tag color="orange">{opt ? opt.label : val}</Tag>;
                                        }}
                                    />
                                    <Column width="80px" title="Thứ tự" dataIndex="thuTuHienThi" key="thuTuHienThi" align="center" />
                                    <Column
                                        width="150px"
                                        title="Thao tác"
                                        key="action"
                                        align="center"
                                        render={(value: DMBopPhanKiemKe) => (
                                            <Space>
                                                <Button
                                                    type="primary"
                                                    size={"small"}
                                                    title="Sửa"
                                                    icon={<EditOutlined rev="" />}
                                                    onClick={() => { setFormDetailModal(value); }}
                                                >
                                                    Sửa
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    danger
                                                    size={"small"}
                                                    title="Xóa"
                                                    onClick={() => { setIdXoa(value.id); }}
                                                    icon={<DeleteOutlined rev="" />}
                                                >
                                                    Xóa
                                                </Button>
                                            </Space>
                                        )}
                                    />
                                </Table>
                            </Col>
                        </Row>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default QuanLyDMBoPhanKiemKe;